import graphql__da6cde8db4745693443a from "./__generated__/AppManagementSceneQuery.graphql.ts";
import graphql__73a2e1de1caa2ad82b0f from "./__generated__/AppManagementSceneVisitMutation.graphql.ts";import { usePendingCursorOperation } from '@dittolive/react-ditto'
import graphql from 'babel-plugin-relay/macro'
import { AnnouncementsContainer } from 'components/announcement'
import { SystemCollections } from 'components/dittoAppConnection'
import { useAppToDatabaseRename } from 'components/hooks'
import { usePrefetchQuery } from 'components/relay'
import TabsWrapper from 'components/tabs'
import { useApplyTenant } from 'components/tenant'
import AppContext from 'context'
import paths from 'paths'
import { Suspense, useContext, useEffect, useMemo } from 'react'
import {
  PreloadedQuery,
  usePreloadedQuery,
  useQueryLoader,
} from 'react-relay/hooks'
import { usePortalMutation } from 'utils'
import { useFeatureFlags } from 'utils/featureFlags'

import { AppManagementSceneQuery } from './__generated__/AppManagementSceneQuery.graphql'
import { AppManagementSceneVisitMutation } from './__generated__/AppManagementSceneVisitMutation.graphql'
import AppHeader from './AppHeader'
import AppManagementContextProvider from './AppManagementContextProvider'
import AppManagementRouter from './AppManagementRouter'
import { useAppURLParams } from './hooks'
import {
  AppAuthAPIConnectionSectionQuery,
  AppAuthRouterQuery,
} from './tabs/auth'
import {
  AppDangerSectionQuery,
  AppSettingsFormQuery,
  TransferAppSectionQuery,
} from './tabs/settings'

export const query = graphql__da6cde8db4745693443a

const visitMutation = graphql__73a2e1de1caa2ad82b0f

type ContainerProps = {
  /** Current app ID. */
  appId: string
  /** True if the domain status is ready since the first moment the status is queried. */
  isDomainInitiallyReady: boolean
  /** True if the ingress domain for the app is ready. */
  isDomainReady: boolean
  /** True if the ingress domain for the app is in error state. */
  isDomainInError: boolean
  /** Number of times the domain status has been queried. */
  queryAttempts: number
}

/** Scene for managing apps */
export default function AppManagementSceneContainer(props: ContainerProps) {
  const [queryRef, loadQuery] = useQueryLoader<AppManagementSceneQuery>(query)

  const { appId } = props
  useEffect(() => {
    loadQuery({ appId }, { fetchPolicy: 'store-and-network' })
  }, [appId, loadQuery])

  if (!queryRef) {
    return null
  }

  return <AppManagementScene {...props} queryRef={queryRef} />
}

type Props = {
  queryRef: PreloadedQuery<AppManagementSceneQuery>
} & ContainerProps

function AppManagementScene({
  queryRef,
  appId,
  isDomainInitiallyReady,
  isDomainReady,
  isDomainInError,
  queryAttempts,
}: Props) {
  const params = useAppURLParams()
  const { isSuperAdmin } = useContext(AppContext)
  const { isRenamed } = useAppToDatabaseRename()
  const {
    viewer: { appById: app },
  } = usePreloadedQuery(query, queryRef)
  const { metrics } = useFeatureFlags()
  const { documents: sinks } = usePendingCursorOperation({
    path: appId,
    collection: SystemCollections.LIVE_QUERY_SINKS_COLLECTION,
    limit: 1,
  })

  const viewerCanUpdateApp = app?.viewerCanUpdate ?? false
  const viewerCanReadAppData = app?.viewerCanReadAppData ?? false
  const viewerCanReadStreams = app?.viewerCanReadStreams ?? false
  const viewerCanReadLicense = app?.viewerCanReadLicense ?? false
  const viewerCanReadApiKey = app?.viewerCanReadApiKey ?? false
  const isDedicatedCluster = !!app?.hydraCluster?.isDedicated
  const isValet = !!app?.hydraCluster?.isValet

  const viewerCanReadMongoConnector = !!app?.viewerCanReadMongoConnector
  const viewerCanAccessMongo =
    viewerCanReadMongoConnector && isValet && isDedicatedCluster

  const viewerHasOwnerPrivileges = !!isSuperAdmin || !!app?.viewerOwnsApp

  // If a viewer doesn't have access to any of the actions in the admin settings,
  // we won't bother showing the admin settings tab.
  const viewerCanAccessAdminSettings =
    viewerHasOwnerPrivileges ||
    !!app?.viewerCanTransfer ||
    !!app?.transferRequestDestination ||
    !!app?.viewerCanDelete

  const hasConfiguredStream = !!sinks.length
  const streamsEnabled = isDedicatedCluster || hasConfiguredStream
  const allowStreams = streamsEnabled && viewerCanReadStreams

  const allowMetrics =
    !!(isSuperAdmin || app?.viewerCanAccessMetrics) && !!metrics
  useApplyTenant(params.organizationSlug!)
  const { prefetchQueries } = usePrefetchQuery()

  const [markAppAsVisited] = usePortalMutation<AppManagementSceneVisitMutation>(
    visitMutation,
    {
      mutationName: 'VisitApp',
      sentryContext: {
        component: 'AppManagementScene',
        appId,
      },
    },
  )

  useEffect(() => {
    if (app) {
      markAppAsVisited({
        variables: { appId },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app])

  const showCollections = viewerCanReadAppData || !!isSuperAdmin
  const showDevices = viewerCanReadAppData || !!isSuperAdmin

  const atLeastOneAuthSection =
    viewerCanReadLicense || viewerCanReadApiKey || viewerCanReadAppData
  const showAuth = atLeastOneAuthSection || !!isSuperAdmin

  const tabItems = useMemo(
    () => [
      ...(showAuth
        ? [
            {
              key: 'connect',
              text: 'Connect',
              to: paths.appManagement({
                ...params,
                appSlug: params.appSlug || '',
                tenantId: params.userId,
                area: 'connect',
                isRenamed,
              }),
              onMouseEnter: () =>
                prefetchQueries(
                  [AppAuthAPIConnectionSectionQuery, AppAuthRouterQuery],
                  { appId },
                ),
            },
          ]
        : []),
      ...(isDomainReady && showCollections
        ? [
            {
              key: 'collections',
              text: 'Collections',
              to: paths.appManagement({
                ...params,
                appSlug: params.appSlug || '',
                tenantId: params.userId,
                area: 'collections',
                isRenamed,
              }),
            },
          ]
        : []),
      ...(isDomainReady && showDevices
        ? [
            {
              key: 'devices',
              text: 'Devices',
              to: paths.appManagement({
                ...params,
                appSlug: params.appSlug || '',
                tenantId: params.userId,
                area: 'devices',
                isRenamed,
              }),
            },
          ]
        : []),
      ...(allowMetrics
        ? [
            {
              key: 'metrics',
              text: 'Metrics',
              to: paths.appManagement({
                ...params,
                appSlug: params.appSlug || '',
                tenantId: params.userId,
                area: 'metrics',
                isRenamed,
              }),
            },
          ]
        : []),
      ...(process.env.NODE_ENV !== 'production'
        ? [
            {
              key: 'issues',
              text: 'Issues',
              to: paths.appManagement({
                ...params,
                appSlug: params.appSlug || '',
                tenantId: params.userId,
                area: 'issues',
                isRenamed,
              }),
            },
          ]
        : []),
      ...(isDomainReady && allowStreams
        ? [
            {
              key: 'cdc',
              text: 'Change Data Capture',
              to: paths.appManagement({
                ...params,
                appSlug: params.appSlug || '',
                tenantId: params.userId,
                area: 'cdc',
                isRenamed,
              }),
            },
          ]
        : []),
      {
        key: 'settings',
        text: 'Settings',
        to: paths.appManagement({
          ...params,
          appSlug: params.appSlug || '',
          tenantId: params.userId,
          area: 'settings',
          isRenamed,
        }),
        onMouseEnter: () =>
          prefetchQueries(
            [
              AppSettingsFormQuery,
              AppDangerSectionQuery,
              TransferAppSectionQuery,
            ],
            {
              appId,
            },
          ),
      },
    ],
    [
      params,
      isDomainReady,
      showDevices,
      allowMetrics,
      prefetchQueries,
      appId,
      showCollections,
      allowStreams,
      showAuth,
      isRenamed,
    ],
  )

  const renderAppContents = () => (
    <>
      <AnnouncementsContainer announcements={app?.announcements} />
      <Suspense fallback={null}>
        <AppHeader app={app} />
      </Suspense>
      {tabItems.length > 1 && <TabsWrapper items={tabItems} />}
      <Suspense fallback={null}>
        <AppManagementRouter appId={appId} />
      </Suspense>
    </>
  )

  return (
    <AppManagementContextProvider
      appId={appId}
      isDomainInitiallyReady={isDomainInitiallyReady}
      isDomainReady={isDomainReady}
      isDomainInError={isDomainInError}
      queryAttempts={queryAttempts}
      isDedicatedCluster={isDedicatedCluster}
      allowStreams={allowStreams}
      viewerCanAccessMetrics={allowMetrics}
      viewerCanReadAppData={viewerCanReadAppData}
      viewerCanReadLicense={viewerCanReadLicense}
      viewerCanReadApiKey={viewerCanReadApiKey}
      viewerCanUpdateApp={viewerCanUpdateApp}
      viewerCanAccessAdminSettings={viewerCanAccessAdminSettings}
      viewerCanAccessMongo={viewerCanAccessMongo}
    >
      {renderAppContents()}
    </AppManagementContextProvider>
  )
}
